/**
 * Read-only preview of an email notification template (ActivityDefinition),
 * rendered with catalog/example variable values — the same rendering used by
 * the notification template editor's "Preview" tab (see
 * `renderPreviewWithCatalogDefaults` / `sanitizeHtml`). Used by callers that
 * only hold a template reference (e.g. the Rules Engine send-email action
 * editor) and don't need the full editor's draft/save state.
 */
import { useMemo } from 'react';
import { Box, Divider, Drawer, Group, Paper, Stack, Text } from '@mantine/core';
import type { ActivityDefinition } from '@medplum/fhirtypes';

import { activityDefinitionToDraft } from '../notification-template.medplum';
import { renderPreviewWithCatalogDefaults } from '../services/notification-variable-resolver';
import { wrapEmailHtml } from '../services/notification-email-html';

interface EmailTemplatePreviewDrawerProps {
  opened: boolean;
  onClose: () => void;
  template: ActivityDefinition | null;
}

export function EmailTemplatePreviewDrawer({
  opened,
  onClose,
  template,
}: EmailTemplatePreviewDrawerProps): JSX.Element {
  const draft = useMemo(() => (template ? activityDefinitionToDraft(template) : null), [template]);

  const preview = useMemo(() => {
    if (!draft) return null;
    const rendered = renderPreviewWithCatalogDefaults(draft.subject, draft.htmlBody, draft.textBody, draft.variables);
    return { ...rendered, htmlBody: wrapEmailHtml(rendered.htmlBody) };
  }, [draft]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={draft ? `Preview: ${draft.name}` : 'Preview email template'}
      position="right"
      size="lg"
    >
      {!draft || !preview ? (
        <Text size="sm" c="dimmed">Select a template to preview.</Text>
      ) : (
        <Stack gap="lg">
          {preview.unresolvedRequired.length > 0 && (
            <Paper withBorder p="sm" radius="sm" style={{ borderColor: 'var(--mantine-color-yellow-6)' }}>
              <Text size="sm" fw={500} c="yellow.8">
                Unresolved required variables: {preview.unresolvedRequired.join(', ')}
              </Text>
              <Text size="xs" c="dimmed">Shown below with placeholder example values.</Text>
            </Paper>
          )}

          <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
              <Group gap="sm">
                <Text size="xs" fw={600} c="dimmed" w={60}>From:</Text>
                <Text size="sm">
                  {draft.from.name ? `${draft.from.name} <${draft.from.email ?? ''}>` : draft.from.email || '—'}
                </Text>
              </Group>
              <Group gap="sm">
                <Text size="xs" fw={600} c="dimmed" w={60}>Subject:</Text>
                <Text size="sm" fw={500}>{preview.subject || '—'}</Text>
              </Group>
              <Divider />
              <Text size="xs" c="dimmed">
                Preview shows sanitized HTML with example variable values — scripts and unsafe content are stripped.
              </Text>
              <iframe
                srcDoc={preview.htmlBody || '<p style="color: #999; font-family: sans-serif">Email body preview will appear here</p>'}
                sandbox=""
                title="Email preview"
                style={{
                  width: '100%',
                  minHeight: 300,
                  border: '1px solid var(--mantine-color-gray-3)',
                  borderRadius: 8,
                  backgroundColor: '#fff',
                }}
              />
            </Stack>
          </Paper>

          {preview.textBody && (
            <Paper withBorder p="md" radius="md">
              <Stack gap="sm">
                <Text size="sm" fw={500}>Plain Text Fallback</Text>
                <Box
                  style={{
                    padding: 12,
                    backgroundColor: 'var(--mantine-color-gray-0)',
                    borderRadius: 6,
                    fontFamily: 'monospace',
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {preview.textBody}
                </Box>
              </Stack>
            </Paper>
          )}
        </Stack>
      )}
    </Drawer>
  );
}
